import React, { useMemo } from 'react';
import { AutocompletAble } from 'ui/DropdownItems/types';
import { useModalOptionsContext } from '../optionsContext';
import CommonProjectionVentilationsCellRenderer, {
    ProjectionVentilationRendererParamsT,
} from './CommonProjectionsVentilationsCellRenderer';
import { buildAvailableVentilationsValues } from './helpers/projectionVentilationsOptionsHelpers';
import {
    VentilationType,
    useUpdateProjectionVentilationMutation,
} from './hooks/hooks';
import { useIntegrationSoftwares } from 'helpers/integrations';
import { trackEventPluriannualVentilationUpdated } from '../../../trackersHelpers';
import { useTranslation } from 'react-i18next';
import { ProjectionVentilation } from 'routes/BudgetModule/components/BudgetProposal/Resource/Projection/helpers/types';
import { useSelectSecondaryBudgetAxisSourceTable } from 'routes/BudgetModule/components/BudgetWithProvider/SecondaryBudgetAxisSourceTableContext';
import { SecondaryBudgetAxisSources } from 'routes/Support/components/ClientSupport/components/BudgetConfiguration/types';
import {
    BUDGET_LINE_PROGRAM_SOFTWARE_ID,
    BUDGET_LINE_PROGRAM_TABLE,
    OPERATION_SEGMENT_SOFTWARE_ID,
    OPERATION_SEGMENT_TABLE,
    OPERATION_SOFTWARE_ID,
    OPERATION_TABLE,
} from 'routes/BudgetModule/config/constants';
import { ProjectionTypes } from 'routes/Admin/Budget/BudgetManagement/BudgetList/CellRenderers/constants';
import { sortAlphabetically } from 'ui/DropdownItems/helpers';

/**
 * Sorts the options alphabetically and puts the fixedProjectionVentilation at the top of the list
 * @returns a list of options sorted alphabetically with all the fixedProjectionVentilation disabled at the top of the list
 */
const sortOptions = (
    options: AutocompletAble<ProjectionVentilation.SoftwareIds>[],
    fixedProjectionVentilation: ProjectionVentilation.SoftwareIds[],
): AutocompletAble<ProjectionVentilation.SoftwareIds>[] => {
    const fixedOptions = options.filter((option) =>
        fixedProjectionVentilation.includes(option.value),
    );

    if (!fixedOptions) {
        return sortAlphabetically(options);
    }

    // Map fixedOptions to new objects with disabled property modified
    const disabledOptions = fixedOptions.map((option) => ({
        ...option,
        disabled: true,
    }));

    const values = [
        ...disabledOptions,
        ...sortAlphabetically(options).filter(
            (option) => !fixedOptions.includes(option),
        ),
    ];

    return values;
};

const getProjectionColumnForSourceTable = (
    sourceTable: SecondaryBudgetAxisSources,
) => {
    const mapping: Record<
        SecondaryBudgetAxisSources,
        ProjectionVentilation.SoftwareIds
    > = {
        [OPERATION_TABLE]: OPERATION_SOFTWARE_ID,
        [OPERATION_SEGMENT_TABLE]: OPERATION_SEGMENT_SOFTWARE_ID,
        [BUDGET_LINE_PROGRAM_TABLE]: BUDGET_LINE_PROGRAM_SOFTWARE_ID,
    };
    return mapping[sourceTable];
};

export default function PluriannualProjectionVentilationsCellRenderer({
    data,
    value: projectionVentilations,
}: ProjectionVentilationRendererParamsT): React.ReactElement {
    const { t } = useTranslation();
    const budgetProposal = data!;
    const { ventilationOptions } = useModalOptionsContext();
    const projectionType = ProjectionTypes.PluriannualProjection;
    const dropDownTitle = t(
        'adminBudgetSpace.projection.pluriannualProjectionDropdownTitle',
    );
    const updateProjectionByMutation = useUpdateProjectionVentilationMutation(
        budgetProposal.id,
    );

    const { data: softwares = new Set() } = useIntegrationSoftwares();

    const availableValues = useMemo(
        () =>
            buildAvailableVentilationsValues(
                ventilationOptions,
                budgetProposal.by,
                softwares,
            ),
        [ventilationOptions, budgetProposal.by, softwares],
    );

    const sourceTable = useSelectSecondaryBudgetAxisSourceTable();
    if (softwares === undefined || !projectionVentilations || !sourceTable) {
        return <></>;
    }
    const fixedVentilationField =
        getProjectionColumnForSourceTable(sourceTable);

    const handleVentilationsMutation = (
        selectedVentilations: ProjectionVentilation.SoftwareIds[],
    ) => {
        updateProjectionByMutation.mutate({
            newVentilation: selectedVentilations,
            ventilationFieldTarget: VentilationType.PLURIANNUAL_PROJECTION_BY,
        });
        trackEventPluriannualVentilationUpdated(
            budgetProposal.id,
            budgetProposal.name,
            selectedVentilations,
        );
    };

    return (
        <CommonProjectionVentilationsCellRenderer
            budgetProposal={budgetProposal}
            initialProjectionVentilations={projectionVentilations}
            dropDownTitle={dropDownTitle}
            availableValues={availableValues}
            projectionType={projectionType}
            handleVentilationsMutation={handleVentilationsMutation}
            sortOptions={(option) =>
                sortOptions(option, [fixedVentilationField])
            }
        />
    );
}
